import { AuthContext } from "@/context/AuthContext";
import ConductionAPI from "@/javascript/conduction-api";
import { LiveEvent } from "@/javascript/user-objects";
import { Utils } from "@/javascript/utils";
import { Box, Button, Flex, Stack, Text, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconAlertCircle, IconCheck } from "@tabler/icons-react";
import { useContext } from "react";
import ConductionModal from "../ConductionModal";

interface LiveEventDeletionFormProps {
	liveEvent: LiveEvent;
	onClose: () => void;
	onDeleted: () => void;
}

const LiveEventDeletionForm: React.FC<LiveEventDeletionFormProps> = ({ liveEvent, onClose, onDeleted }) => {
	const auth = useContext(AuthContext);
	if (!auth) {
		throw new Error("LiveEventDeletionForm must be a child of AuthContext.");
	}
	const token = auth.token;
	if (!token) return null;

	// Set up the form; we only need one field where the user confirms the live event name.
	const form = useForm({
		initialValues: {
			liveEventName: "",
		},
		validate: {
			liveEventName: (value) =>
				value.trim().length === 0
					? "You must type the live event name to confirm deletion."
					: liveEvent !== null && value.trim() !== liveEvent.name.trim()
						? "Entered live event name does not match."
						: null,
		},
	});

	const handleSubmit = async () => {
		if (!form.isValid()) {
			form.validate();
			return;
		}

		// Display a notification that deletion is in progress.
		const notificationId = Utils.displayNotification(
			"Deleting Live Event",
			"Deleting live event, please wait...",
			"blue",
			"loading"
		);

		try {
			// Call the API to delete the live event.
			await ConductionAPI.deleteLiveEvent(liveEvent.id, token);

			Utils.updateNotification(
				notificationId,
				"Live Event Deleted",
				"The live event was deleted.",
				"green",
				<IconCheck size={16} />
			);
			onDeleted();
			onClose();
		} catch (error) {
			console.error("Error deleting live event", error);
			Utils.updateNotification(
				notificationId,
				"Error",
				"Failed to delete the live event.",
				"red",
				<IconAlertCircle size={16} />,
				true
			);
		}
	};

	const height = 20; // view height (vh)

	return (
		<ConductionModal
			onClose={onClose}
			title="Delete Live Event"
			size="md"
		>
			<Box style={{ minHeight: `${height}vh` }}>
				<form onSubmit={form.onSubmit(handleSubmit)}>
					<Flex direction="column" justify="space-between" align="stretch" style={{ minHeight: `${height}vh` }}>
						<Stack>
							<Text>
								Please type the exact name of the live event, "{liveEvent.name}", to confirm deletion:
							</Text>
							<TextInput
								label="Live Event Name"
								placeholder={liveEvent.name}
								required
								{...form.getInputProps("liveEventName")}
							/>
						</Stack>
						<Button type="submit" radius="xl" size="sm" loading={form.submitting} color="red">
							Delete Live Event
						</Button>
					</Flex>
				</form>
			</Box>
		</ConductionModal>
	);
};

export default LiveEventDeletionForm;
